import { Controller } from "@hotwired/stimulus";
import * as AgGrid from "ag-grid-community";
import * as datasource from "grid/datasource";
import * as gridUtils from "grid/utils";
import { ColumnsState } from "grid/columns_state";
import { DEFAULT_COL_DEF } from "grid/columns";

interface RecurringPaymentReportRow {
  account_number: string;
  amount: string;
  convenience_fee: string;
  date_created: string;
  end_date: string;
  id: string;
  invoice_number: string;
  last_payment_date: string;
  last_payment_status: string;
  payment_channel: string;
  payment_method: string;
  phone_number: string;
  reconciliation_user: string;
  start_date: string;
  state: string;
}

export default class extends Controller {
  static targets = ["grid", "tableWrapper"];
  static values = {
    i18nJson: String,
    indexJsonUrl: String,
    pageSize: Number,
  };

  declare readonly gridTarget: HTMLElement;
  declare readonly tableWrapperTarget: HTMLElement;
  declare readonly hasTableWrapperTarget: boolean;
  declare readonly i18nJsonValue: string;
  declare readonly indexJsonUrlValue: string;
  declare readonly pageSizeValue: number;

  private gridApi?: AgGrid.GridApi<RecurringPaymentReportRow>;
  private dataSource?: datasource.Datasource;

  search(event: Event) {
    event.preventDefault();
    const form = this.element.querySelector("form");
    if (!form) {
      return;
    }

    if (!this.hasTableWrapperTarget) {
      return;
    }

    const listUrl = this.#buildListUrl(form as HTMLFormElement);
    this.tableWrapperTarget.classList.remove("hidden");

    if (this.gridApi && this.dataSource) {
      this.dataSource.indexUrl = listUrl;
      this.gridApi.purgeInfiniteCache();
    } else {
      this.#initGrid(listUrl);
    }
  }

  disconnect() {
    this.gridApi?.destroy();
  }

  #buildListUrl(form: HTMLFormElement): string {
    const url = new URL(this.indexJsonUrlValue, window.location.origin);
    const fd = new FormData(form);
    for (const [key, value] of fd.entries()) {
      if (key === "authenticity_token") {
        continue;
      }
      if (typeof value === "string" && value !== "") {
        url.searchParams.append(key, value);
      }
    }
    return url.toString();
  }

  #initGrid(indexUrl: string) {
    const colDefs: AgGrid.ColDef<RecurringPaymentReportRow>[] = [
      { field: "id", ...DEFAULT_COL_DEF, sortable: false },
      { field: "account_number", ...DEFAULT_COL_DEF, sortable: false },
      { field: "invoice_number", ...DEFAULT_COL_DEF, sortable: false },
      { field: "amount", ...DEFAULT_COL_DEF, sortable: false },
      { field: "convenience_fee", ...DEFAULT_COL_DEF, sortable: false },
      { field: "date_created", ...DEFAULT_COL_DEF, sortable: false },
      { field: "start_date", ...DEFAULT_COL_DEF, sortable: false },
      { field: "end_date", ...DEFAULT_COL_DEF, sortable: false },
      { field: "last_payment_date", ...DEFAULT_COL_DEF, sortable: false },
      { field: "last_payment_status", ...DEFAULT_COL_DEF, sortable: false },
      { field: "payment_method", ...DEFAULT_COL_DEF, sortable: false },
      { field: "payment_channel", ...DEFAULT_COL_DEF, sortable: false },
      { field: "reconciliation_user", ...DEFAULT_COL_DEF, sortable: false },
      { field: "phone_number", ...DEFAULT_COL_DEF, sortable: false },
      { field: "state", ...DEFAULT_COL_DEF, sortable: false },
    ];

    const dataSource = new datasource.Datasource({
      callbacks: {},
      indexPath: indexUrl,
      limit: String(this.pageSizeValue),
    });

    const options: AgGrid.GridOptions<RecurringPaymentReportRow> = {
      ...gridUtils.DEFAULT_GRID_OPTIONS,
      cacheBlockSize: this.pageSizeValue,
      columnDefs: gridUtils.buildColumnDefs(colDefs, { i18n: JSON.parse(this.i18nJsonValue) }),
      datasource: dataSource,
    };

    const gridApi = AgGrid.createGrid(this.gridTarget, options);
    dataSource.gridApi = gridApi;
    this.dataSource = dataSource;
    this.gridApi = gridApi;

    const columnsState = new ColumnsState(gridApi, this.identifier);
    columnsState.initialize();
    gridApi.addEventListener("columnResized", (e) => columnsState.onResize(e));
    const resetBtn = this.element.querySelector(".reset-grid") as HTMLButtonElement | null;
    resetBtn?.addEventListener("click", () => columnsState.reset());
  }
}
